// React & Vendor Libs
const { useState, useMemo, useEffect, useCallback } = wp.element;

// NekoUI
import { NekoButton, NekoBlock, NekoWrapper, NekoColumn, NekoTable, useNekoTasks, NekoPaging,
  NekoQuickLinks, NekoLink, NekoTheme, NekoCheckbox } from '@neko-ui';
import { useKeyboardEvent } from '@neko-ui';
import useDatabaseCleanerContext, { cronJobsColumns, cronsFilters, hideUsedByWordPressLabel } from '../context';
import { SectionButtonStyle, limitSelectElement,
  SectionFooterStyle, UsedByField, BulkUsedByButton, FilterSearchField, NameField } from './common';
import { formatUnixtime } from './../helper';
import i18n from '../i18n';
import { useShiftSelectable } from './hooks';

const usedByKey = 'cron_usedby';
const SettingsCronJobs = () => {

  const { busyAction, filteredCronJobs, busyCleanBulkTasks, busyCronJobs, cronJobsFilters,
    cronJobsTotal: total, plugins, cronJobsUsedBy } = useDatabaseCleanerContext();
  const { deleteCron, deleteCrons, resetBusyBulkTasks, updateOption, toggleModal,
    setBusyAction, setShouldFetchCronJobs, mutateCronJobs, pushBusyCronJobs, popBusyCronJobs,
    setSelectedUsedBy, updateCronJobsFilters } = useDatabaseCleanerContext();
  const bulkTasks = useNekoTasks();

  const [ selectedCronJobs, setSelectedCronJobs ] = useState([]);
  const allChecked = useMemo(() => selectedCronJobs.length === filteredCronJobs.length, [selectedCronJobs, filteredCronJobs]);

  const { pressShift } = useKeyboardEvent();
  const { onCheckboxChange } = useShiftSelectable({
    pressShift,
    list: filteredCronJobs,
    selectedList: selectedCronJobs,
    setSelectedList: setSelectedCronJobs,
    key: 'cron_name'
  });

  useEffect(() => {
    setShouldFetchCronJobs();
  }, []);

  useEffect(() => {
    return () => {
      if (busyCleanBulkTasks) {
        return;
      }
      bulkTasks.stop();
      resetBusyBulkTasks();
      setBusyAction([]);
    }
  }, [busyCleanBulkTasks]);

  const deleteBusyItems = useMemo(() => {
    const cronNames = filteredCronJobs.map(v => v.cron_name);
    return busyAction.filter(busy => cronNames.includes(busy.item));
  }, [busyAction, filteredCronJobs]);

  const isBulkDeleteDisabled = useMemo(() =>
    selectedCronJobs.some(v => v.slug === 'wordpress'),
    [selectedCronJobs]
  );

  const data = useMemo(() => {
    return (
      filteredCronJobs.map(v => {
        const checked = selectedCronJobs.some(entry => entry.name === v.cron_name);
        const onChange = (isChecked, id) => onCheckboxChange(isChecked, v);
        const isDeleteDisabled = v.info.usedBy.toLowerCase() === 'wordpress';
        const disabled = busyAction.some(busy => busy.item === v.cron_name);

        return {
          name: <NameField name={v.cron_name + v.unixtime} item={v} itemText={v.cron_name}
            onChange={onChange} checked={checked} isBusy={checked && disabled} />,
          usedBy: <UsedByField item={v} itemName={v.cron_name} itemsUsedBy={cronJobsUsedBy}
            optionName={usedByKey} plugins={plugins} setSelectedUsedBy={setSelectedUsedBy}
            toggleModal={toggleModal} updateOption={updateOption}
            onRefresh={() => { onRefresh(); }}
          />,
          schedule: v.schedule ?? '-',
          nextRun: formatUnixtime(v.unixtime),
          actions: <NekoButton className={!isDeleteDisabled ? 'danger' : 'primary'} icon="close"
            onClick={() => {
              const item = { name: v.cron_name, args: v.args };
              deleteCron({ item });
            }}
            busyIconSize="100%" style={{ minWidth: 40, width: 40 }}
            disabled={disabled || isDeleteDisabled} isBusy={disabled}>
          </NekoButton>
        }
      })
    );
  }, [filteredCronJobs, busyAction, selectedCronJobs, cronJobsUsedBy, onCheckboxChange]);

  const onUpdateLimit = useCallback(async(value, name) => {
    updateOption({ param: { name, value }});
  }, [updateOption]);

  const onRefresh = useCallback(async (clearSelectedItems = false) => {
    pushBusyCronJobs();
    await mutateCronJobs();
    if (clearSelectedItems) {
      setSelectedCronJobs([]);
    }
    popBusyCronJobs();
  }, [mutateCronJobs]);

  const onBulkDeleteClick = useCallback(() => {
    toggleModal({modal: 'deleteAll', enable: true, onNext: async () => {
      const newSelectedCronJobs = await deleteCrons({ items: selectedCronJobs.map(v => { return { name: v.name, args: v.args } }) });
      if (newSelectedCronJobs.length) {
        setSelectedCronJobs(newSelectedCronJobs);
        return;
      }
      setSelectedCronJobs([]);
    }});
  }, [selectedCronJobs]);

  const columns = useMemo(() => {
    const i = cronJobsColumns.findIndex(v => v.accessor === 'name');
    const j = cronJobsColumns.findIndex(v => v.accessor === 'actions');
    const onChange = (checked) => {
      setSelectedCronJobs(checked ? filteredCronJobs.map(v => {
        return { name: v.cron_name, args: v.args, info: v.info };
      }) : [])
    }
    cronJobsColumns[i].title = <>
      <NekoCheckbox name="all-crons" checked={allChecked} onChange={onChange} />
      Name
    </>;
    cronJobsColumns[j].title = '';

    if (!selectedCronJobs.length)
      return cronJobsColumns;

    const actionsTitle = <>
      <NekoButton icon="close" title={i18n.INFO_BULK_DELETE_ITEMS} style={{ minWidth: 40, width: 40 }}
        className={!isBulkDeleteDisabled ? 'danger' : 'primary'} busyIconSize="100%"
        onClick={onBulkDeleteClick} isBusy={deleteBusyItems.length > 1}
        disabled={deleteBusyItems.length||isBulkDeleteDisabled} />
    </>;
    cronJobsColumns[j].title = actionsTitle;
    return cronJobsColumns;
  }, [cronJobsColumns, allChecked, filteredCronJobs, selectedCronJobs, deleteBusyItems, isBulkDeleteDisabled, onBulkDeleteClick]);

  const limitSelect = useMemo(() => {
    return limitSelectElement("cron_jobs_limit", cronJobsFilters.limit, (value, name) => {
      setSelectedCronJobs([]);
      updateCronJobsFilters({ limit: value, page: 1 });
      onUpdateLimit(value, name);
    });
  }, [cronJobsFilters.limit, updateCronJobsFilters, onUpdateLimit]);

  const paging = useMemo(() => {
    const { page, limit, filterBy, hideUsedByWordPress } = cronJobsFilters;
    const pagingTotal = total[filterBy][hideUsedByWordPress];
    return <NekoPaging currentPage={page} limit={limit} total={pagingTotal} onClick={(page) => { setSelectedCronJobs([]); updateCronJobsFilters({ page }); }} />
  }, [cronJobsFilters.page, cronJobsFilters.limit, total, updateCronJobsFilters]);

  const currentSort = useMemo(() => {
    return { by: cronJobsFilters.sort, accessor: cronJobsFilters.accessor };
  }, [cronJobsFilters.accessor, cronJobsFilters.sort]);

  useEffect(() => {
    setSelectedCronJobs([]);
  }, [currentSort]);

  return (
    <NekoWrapper>

      <NekoColumn minimal>
        <NekoBlock busy={busyCronJobs} title="Cron Jobs" className="primary" action={
          <BulkUsedByButton itemsUsedBy={cronJobsUsedBy} selectedItems={selectedCronJobs}
            optionName='cron_usedby' plugins={plugins} setSelectedUsedBy={setSelectedUsedBy}
            toggleModal={toggleModal} updateOption={updateOption} onRefresh={() => { onRefresh(true); }}
          />
        }>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <NekoQuickLinks value={cronJobsFilters.filterBy} 
              onChange={(value) => { setSelectedCronJobs([]); updateCronJobsFilters({ filterBy: value, page: 1 }); }}>
              {cronsFilters.map(v => 
                <NekoLink title={v.title} value={v.value} count={total[v.value][cronJobsFilters.hideUsedByWordPress]}
                  variant={v.filterValue === 'warn' && total[v.value][cronJobsFilters.hideUsedByWordPress] > 0 ? 'danger' : 'primary'} />)
              }
            </NekoQuickLinks>
            <div style={{ display: 'flex' }}>
              <NekoButton icon="replay" color={NekoTheme.white} style={{...SectionButtonStyle, color: NekoTheme.blue}} onClick={onRefresh} />
              {paging}
            </div>
          </div>
          <NekoTable variant="raw" alternateRowColor
            data={data} columns={columns} selectedItems={[]}
            sort={currentSort} sortIconColor={NekoTheme.blue}
            onSortChange={(accessor, sort) => updateCronJobsFilters({ accessor, sort })} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            {limitSelect}
            {paging}
          </div>
          <div style={SectionFooterStyle}>
            <FilterSearchField name='cron_jobs_search' value={cronJobsFilters.search}
              onChange={(value) => updateCronJobsFilters({ search: value, page: 1 })}
              onReset={() => updateCronJobsFilters({ search: "" })}
            />
            <div style={{ flex: 'auto' }} />
            <NekoCheckbox name="cron_jobs_hide_wordpress" label={hideUsedByWordPressLabel}
              checked={cronJobsFilters.hideUsedByWordPress}
              onChange={(value) => updateCronJobsFilters({ hideUsedByWordPress: value, page: 1 })}
            />
          </div>
        </NekoBlock>
      </NekoColumn>

    </NekoWrapper>
  );
};

export default SettingsCronJobs;